import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import moment from "moment";

import { PageHeaderMui } from "../../components/index.js";
import { Modal, ShieldPersonIcon } from "../../components/mui/index.js";
import ChipStatus from "../../components/mui/status/status.component.js";
import EnhancedTable from "../../components/mui/enhanced-table/enhanced-table.component.js";
import { toasterService } from "../../components/toaster/toaster-service.js";
import {
  archiveRunbook,
  getRunbook,
  getRunbookItems,
  recordResult,
} from "../../shared/release-runbook.api.js";

// Same five the Django page offers, and the same wording, so a tester moving
// between them is not learning two vocabularies.
const TICK_STATES = [
  { state: "pass", label: "Pass" },
  { state: "fail", label: "Fail" },
  { state: "skipped", label: "Skip" },
  { state: "blocked", label: "Blocked" },
  { state: "reset", label: "Un-tick" },
];

// A release is ticked by a room of people at once, so a minute of staleness is
// too long to watch somebody else's progress.
const REFRESH_MS = 10000;

const Field = ({ label, children }) =>
  children ? (
    <Box sx={{ display: "flex", gap: 2, py: 0.5 }}>
      <Typography sx={{ minWidth: "80px", color: "#848484", fontSize: "13px" }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1 }}>{children}</Box>
    </Box>
  ) : null;

const Command = ({ children }) =>
  children ? (
    <Typography
      sx={{
        fontFamily: "monospace",
        fontSize: "13px",
        background: "#F3F3F3",
        p: 1,
        borderRadius: "4px",
        overflowX: "auto",
      }}
    >
      {children}
    </Typography>
  ) : null;

export const RunbookDetail = () => {
  const { code } = useParams();

  const [runbook, setRunbook] = useState(null);
  const [openItem, setOpenItem] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [renderIteration, setRenderIteration] = useState(0);

  const loadHeader = useCallback(() => {
    getRunbook(code)
      .then(({ data }) => setRunbook(data))
      .catch(() => toasterService.error(`Could not load runbook ${code}.`));
  }, [code]);

  useEffect(loadHeader, [loadHeader]);

  // Bound to the code rather than curried at render, so the table is not handed a
  // new api function on every keystroke in the note field.
  const itemsApi = useCallback((params) => getRunbookItems(code, params), [code]);

  const tick = (state) => {
    setSaving(true);
    recordResult({ item: openItem.id, state, note })
      .then(() => {
        toasterService.success(`Recorded ${state} for ${openItem.title}.`);
        setOpenItem(null);
        setNote("");
        // The header carries the ticked count, so it moves with the table.
        loadHeader();
        setRenderIteration((n) => n + 1);
      })
      .catch(() => toasterService.error("That tick was not recorded."))
      .finally(() => setSaving(false));
  };

  const archive = useCallback(() => {
    setArchiving(true);
    archiveRunbook(code)
      .then(() => {
        toasterService.success("Archived. The snapshot is in S3.");
        loadHeader();
      })
      .catch((e) => {
        const detail = e?.response?.data?.detail;
        if (e?.response?.status === 409) {
          toasterService.warning(detail || "This environment must not archive.");
        } else {
          toasterService.error(detail || "Nothing was archived.");
        }
      })
      .finally(() => setArchiving(false));
  }, [code, loadHeader]);

  const headCells = [
    {
      noSort: true,
      key: "state",
      label: "State",
      width: "10%",
      displayComponent: ({ data }) => (
        <ChipStatus status={data.latest ? data.latest.state : "not tested"} />
      ),
    },
    {
      key: "title",
      label: "Step",
      width: "30%",
      displayComponent: ({ data }) => (
        <Box>
          <Typography sx={{ fontWeight: "bold" }}>{data.title}</Typography>
          <Typography sx={{ fontSize: "12px", color: "#848484" }}>
            {data.repo_name} &middot; {data.bucket_display}
          </Typography>
        </Box>
      ),
    },
    {
      noSort: true,
      key: "kind_display",
      label: "Kind",
      width: "12%",
      displayComponent: ({ data }) => (
        <Typography sx={{ fontSize: "14px" }}>{data.kind_display || "--"}</Typography>
      ),
    },
    {
      noSort: true,
      key: "author_name",
      label: "Author",
      width: "14%",
      displayComponent: ({ data }) => (
        <Typography sx={{ fontSize: "14px" }}>{data.author_name || "--"}</Typography>
      ),
    },
    {
      noSort: true,
      key: "ticked_by",
      label: "Ticked by",
      width: "20%",
      displayComponent: ({ data }) =>
        data.latest ? (
          <Box>
            <Typography sx={{ fontSize: "14px" }}>{data.latest.user_name}</Typography>
            <Typography sx={{ fontSize: "12px", color: "#848484" }}>
              {moment(data.latest.recorded_when).format("MMM D, h:mm a")}
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize: "14px", color: "#848484" }}>--</Typography>
        ),
    },
    {
      noSort: true,
      key: "note",
      label: "Note",
      width: "14%",
      displayComponent: ({ data }) => (
        <Typography sx={{ fontSize: "13px" }}>
          {data.latest && data.latest.note ? `“${data.latest.note}”` : ""}
        </Typography>
      ),
    },
  ];

  // Memoised because EnhancedTable renders this through createElement: a new arrow
  // on every render is a new component type, which remounts the button each time.
  const actionButtons = useMemo(
    () => ({
      displayComponent: () => (
        <Button variant="contained" disabled={archiving} onClick={archive}>
          Submit and archive
        </Button>
      ),
    }),
    [archiving, archive],
  );

  return (
    <div className="wrapper">
      <PageHeaderMui
        type="Release Runbook"
        name={runbook ? runbook.code : code}
        icon={<ShieldPersonIcon />}
      />
      <Stack spacing={2}>
        <Box sx={{ px: "20px", pt: "20px" }}>
          {runbook && (
            <Stack spacing={1}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <ChipStatus status={runbook.status} />
                <Typography sx={{ fontSize: "14px" }}>
                  {runbook.name} &middot; {runbook.tested_count} of{" "}
                  {runbook.item_count} ticked
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#848484" }}>
                  generated{" "}
                  {moment(runbook.generated_when).format("MMM D, YYYY h:mm a")}
                  {runbook.generated_by_name
                    ? ` by ${runbook.generated_by_name}`
                    : ""}
                </Typography>
              </Box>
              {runbook.repos.map((repo) => (
                <Typography
                  key={repo.name}
                  sx={{ fontSize: "13px", color: "#848484" }}
                >
                  <b>{repo.name}</b> &middot; branches {repo.trunk_ref} &rarr;{" "}
                  {repo.release_ref} &middot; {repo.commit_count} commit
                  {repo.commit_count === 1 ? "" : "s"}
                  {repo.range_description
                    ? ` · ${repo.range_description}`
                    : ""}
                  {repo.previous_release_ref
                    ? ` · previous ${repo.previous_release_ref}`
                    : ""}
                </Typography>
              ))}
            </Stack>
          )}
        </Box>
        <div className="mui-wrapper">
          <EnhancedTable
            type="Steps"
            headCells={headCells}
            api={itemsApi}
            enableAutoRefresh
            autoRefreshInterval={REFRESH_MS}
            renderIteration={renderIteration}
            actionButtons={actionButtons}
            errors={() => toasterService.error("Could not load the steps.")}
            handleRowClick={(row) => {
              setOpenItem(row);
              setNote("");
            }}
          />
        </div>
      </Stack>

      <Modal
        open={!!openItem}
        onClose={() => setOpenItem(null)}
        title={openItem ? openItem.title : ""}
        maxWidth="md"
        actions={
          openItem
            ? TICK_STATES.map(({ state, label }) => (
                <Button
                  key={state}
                  variant="outlined"
                  disabled={saving}
                  onClick={() => tick(state)}
                >
                  {label}
                </Button>
              ))
            : null
        }
      >
        {openItem && (
          <Stack spacing={1} sx={{ py: 1 }}>
            <Field label="state">
              <ChipStatus
                status={openItem.latest ? openItem.latest.state : "not tested"}
              />
            </Field>
            <Field label="key">
              <Typography sx={{ fontFamily: "monospace", fontSize: "13px" }}>
                {openItem.key}
              </Typography>
            </Field>
            <Field label="author">{openItem.author_name}</Field>
            <Field label="why">{openItem.why}</Field>
            <Field label="dry run">
              <Command>{openItem.dry_run}</Command>
            </Field>
            <Field label="real run">
              <Command>{openItem.real_run}</Command>
            </Field>
            <Field label="expect">{openItem.expect}</Field>
            <Field label="stop if">{openItem.stop_if}</Field>
            <TextField
              size="small"
              fullWidth
              placeholder="note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              sx={{ pt: 1 }}
            />
          </Stack>
        )}
      </Modal>
    </div>
  );
};
